import React, {
   CSSProperties,
   FocusEvent,
   forwardRef, MutableRefObject,
   ReactNode,
   Ref, RefObject, useCallback, useContext, useEffect, useImperativeHandle,
   useRef, useState
} from 'react';
import { convertSize } from '../../../../utils/StyleUtils';
import {
   AutoComplete,
   AutoCompleteOption,
   AutoCompleteRefProps
} from '../../../controls/autocomplete';
import { FormContext, generateFormFieldId } from '../../form';

/**
 * {@docCategory AutoCompleteInput}
 */
export interface AutoCompleteInputProps {
   /**
    * The name of the input item.
    */
   name?: string;

   /**
    * Initial selected option
    */
   defaultValue?: string;

   /**
    * The value.
    */
   value?: string;

   /**
    * The placeholder text for the input item.
    */
   placeholder?: string;

   /**
    * Set the input item to disabled.
    * @defaultvalue false
    */
   disabled?: boolean;

   /**
    * Show clear button
    */
   allowClear?: boolean;

   /**
    * Determines if a border should be shown around the component.
    * @defaultvalue true
    */
   border?: boolean;

   /**
    * Determines if the component should have internal padding.
    * @defaultvalue true
    */
   padding?: boolean;

   /**
    * The label text displayed before (on the left side of) the input field.
    */
   addonBefore?: ReactNode;

   /**
    * The label text displayed after (on the right side of) the input field.
    */
   addonAfter?: ReactNode;

   /**
    * Sets the maximum number of characters that can be entered.
    */
   maxLength?: number;

   /**
    * Sets the width.
    */
   width?: string | number;

   /**
    * Sets the min width.
    */
   minWidth?: string | number;

   /**
    * Sets the max width.
    */
   maxWidth?: string | number;

   /**
    * Set this to focus on form item on first render
    */
   autoFocus?: boolean;

   /**
    * AutoComplete options. Use this property to get better performance than jsx
    * child definitions.
    */
   options?: AutoCompleteOption[];

   /**
    * Specify content to show when no result matches
    * @defaultvalue 'Not Found'
    */
   notFoundContent?: ReactNode;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;

   /**
    * Callback called when the value is changed.
    * @param value - The value
    */
   onChange?: (value: string | undefined) => void;

   /**
    * Callback that is executed when select action is made on dropdown items.
    */
   onSelect?: (value: string | undefined) => void;

   /**
    * Callback that is executed when search action is made via button click,
    * Enter key press or on each key press.
    */
   onSearch?: (searchText: string) => void;

   /**
    * Callback called when the component loses focus.
    * @param event - The focus event object
    */
   onBlur?: (event: FocusEvent<HTMLElement>) => void;

   /**
    * Callback called when the component gains focus.
    * @param event - The focus event object
    */
   onFocus?: (event: FocusEvent<HTMLElement>) => void;

   /**
    * Function called when the user presses the enter key.
    */
   onPressEnter?: React.KeyboardEventHandler<HTMLElement>;

   /**
    * Function called when the user presses a key.
    */
   onKeyDown?: React.KeyboardEventHandler<HTMLElement>;

   /**
    * Function called when the dirty state changes.  The input is considered dirty
    * when the raw string value that is currently selected does not match the last
    * good validated value.
    *
    * @param isDirty - True if the raw string value that is currently selected does not match the last good validated value
    */
   onDirtyStateChange?: (isDirty: boolean) => void;
}

const ENTER_KEY_CODE = 13;

const handleDirty = (isDirtyRef: MutableRefObject<boolean>, rawInputText: RefObject<string | undefined>, inputValue: string | undefined, onDirtyStateChange?: (isDirty: boolean)=>void) => {
   const wasDirty = isDirtyRef.current;
   const isDirty = rawInputText.current !== inputValue;
   if (isDirty !== wasDirty) {
      isDirtyRef.current = isDirty;
      if (onDirtyStateChange) {
         onDirtyStateChange(isDirty);
      }
   }
};

const createStyle = (width?: string | number, minWidth?: string | number, maxWidth?: string | number, style?: CSSProperties) => {
   return {
      width: convertSize(width, '250px'),
      minWidth: convertSize(minWidth, '60px'),
      maxWidth: convertSize(maxWidth),
      ...style
   };
};

export const AutoCompleteInput = forwardRef<AutoCompleteRefProps, React.PropsWithChildren<AutoCompleteInputProps>>(
   (props: React.PropsWithChildren<AutoCompleteInputProps>, ref: Ref<AutoCompleteRefProps>) => {

      const [inputValue, setInputValue] = useState<string>();

      const autoCompleteRef = useRef<AutoCompleteRefProps | null>(null);

      // *** These are used only for onDirtyStateChange event
      const rawInputTextRef = useRef<string | undefined>();
      const isDirtyRef = useRef<boolean>(false);
      // ***

      const {
         value: propsValue,
         onDirtyStateChange,
         onBlur,
         onChange,
         onSelect,
         onKeyDown,
         onPressEnter,
         width,
         minWidth,
         maxWidth,
         style: styleProp
      } = props;

      useEffect(() => {
         const value = propsValue;

         setInputValue(value);
         rawInputTextRef.current = value;
      }, [propsValue]);

      // *** Used only for onDirtyStateChange event
      useEffect(() => {
         handleDirty(isDirtyRef, rawInputTextRef, inputValue, onDirtyStateChange);
      }, [inputValue, onDirtyStateChange]);
      // ***

      const inputOnChangeHandler = useCallback((value: string | undefined) => {
         rawInputTextRef.current = value;
         setInputValue(value);
      },[]);

      const propsOnChangeHandler = useCallback(() => {

         const newInputValue = inputValue === undefined ? '' : inputValue;
         if (inputValue !== newInputValue) {
            setInputValue(newInputValue);
         }

         if (onChange) {
            onChange(inputValue);
         }
      },[onChange, inputValue]);

      const onBlurHandler = useCallback((event: FocusEvent<HTMLElement>) => {

         const cancelButtonCausedBlur = (event.relatedTarget && (event.relatedTarget as HTMLElement).getAttribute('data-iscancelbutton'));

         if (!cancelButtonCausedBlur) {
            propsOnChangeHandler();
         }

         if (onBlur) {
            onBlur(event);
         }
      },[onBlur, propsOnChangeHandler]);

      const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLElement>) => {

         if (event.keyCode === ENTER_KEY_CODE) {

            if (onPressEnter) {
               onPressEnter(event);
            }
         }

         if (onKeyDown) {
            onKeyDown(event);
         }
      },[onKeyDown, onPressEnter]);

      const onSelectHandler = useCallback((value: string | undefined) => {
         if (onSelect) {
            onSelect(value);
         }
      },[onSelect]);

      useImperativeHandle(ref, () => ({
         focus: () => {
            if (autoCompleteRef.current) {
               autoCompleteRef.current.focus();
            }
         },
         blur: () => {
            if (autoCompleteRef.current) {
               autoCompleteRef.current.blur();
            }
         },
      } as AutoCompleteRefProps));

      const showBorder = props.border === undefined ? true : props.border;
      const padding = props.padding === undefined ? true : props.padding;
      const classNames = ['uf-autocomplete-input uf-input'];

      const style = createStyle(width,minWidth,maxWidth,styleProp);

      if (showBorder) {
         classNames.push('uf-input-bordered');
      }

      if (props.className) {
         classNames.push(props.className);
      }

      if (props.disabled) {
         classNames.push('uf-input-disabled');
      }

      const formContext = useContext(FormContext);
      const inputId = generateFormFieldId(formContext.name, props.name);

      return (
         <span
            className={classNames.join(' ')}
            style={style}
         >
         <span className={'uf-input-wrapper'}>
            {props.addonBefore && <span className={'uf-input-addon'}>
               {props.addonBefore}
            </span>}

            <span className={`uf-input-control-container ${padding ? 'uf-autocomplete-input-control-container-padding' : ''}`}>

               <AutoComplete
                  id={inputId}
                  autoFocus={props.autoFocus}
                  bordered={false}
                  defaultValue={props.defaultValue}
                  value={inputValue}
                  placeholder={props.placeholder}
                  disabled={props.disabled}
                  allowClear={props.allowClear}
                  options={props.options}
                  size={'middle'}
                  maxLength={props.maxLength}
                  notFoundContent={props.notFoundContent}
                  onSearch={props.onSearch}
                  onSelect={onSelectHandler}
                  onFocus={props.onFocus}
                  onBlur={onBlurHandler}
                  onChange={inputOnChangeHandler}
                  onInputKeyDown={handleKeyDown}
                  className={'uf-input-control'}
                  ref={autoCompleteRef}
               />
            </span>

            {props.addonAfter && <span className={'uf-input-addon'}>
               {props.addonAfter}
            </span>}
         </span>
      </span>
      );
   });
